import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { authService } from '../services/auth';

/**
 * Permissões do usuário logado, para a tela não oferecer o que não funciona.
 *
 * A lista chega no login, mas não pode ficar parada ali: quando o administrador
 * altera as caixas, quem já estava dentro continuaria vendo botões que o
 * servidor recusa. Por isso o provedor reconsulta o servidor ao abrir a
 * aplicação e sempre que a aba volta a ficar em foco, que é quando a pessoa
 * retoma o trabalho.
 *
 * Isto é conveniência de interface: quem decide é o servidor, que confere em
 * toda requisição. Uma lista adulterada aqui não abre porta nenhuma, apenas
 * mostra um botão que vai receber 403.
 */

const lerGuardadas = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem('auth_permissoes') || '[]');
  } catch {
    return [];
  }
};

const PermissoesContexto = createContext<{ lista: string[]; recarregar: () => void }>({
  lista: [],
  recarregar: () => {},
});

export const PermissoesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Começa com o que está guardado para a tela desenhar de imediato, sem piscar
  // enquanto a consulta ao servidor não volta.
  const [lista, setLista] = useState<string[]>(lerGuardadas);

  const recarregar = useCallback(() => {
    if (!localStorage.getItem('auth_token')) return;
    authService.me()
      .then((r) => {
        if (!r.permissoes) return;
        // Compara antes de trocar o estado: a consulta se repete a cada 15
        // segundos, e trocar o array a cada volta redesenharia a tela inteira
        // sem que nada tivesse mudado.
        setLista((atual) => (
          atual.length === r.permissoes!.length && atual.every((p, i) => p === r.permissoes![i])
            ? atual
            : r.permissoes!
        ));
      })
      // Falha aqui não pode derrubar a tela: segue valendo a lista guardada, e
      // o servidor continua barrando o que não é permitido.
      .catch(() => {});
  }, []);

  useEffect(() => {
    recarregar();

    // Consulta periodica enquanto a aba esta visivel. Sem ela, uma permissao
    // retirada so chegaria no proximo carregamento da pagina, e quem estivesse
    // com um formulario aberto continuaria preenchendo algo que o servidor ja
    // recusa. Fica em 15 segundos: e uma consulta leve, e o intervalo mantem a
    // impressao de imediato sem transformar a tela numa fonte de trafego.
    const INTERVALO = 15_000;
    let timer: ReturnType<typeof setInterval> | undefined;

    const iniciar = () => {
      if (timer) return;
      timer = setInterval(recarregar, INTERVALO);
    };

    const parar = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = undefined;
    };

    // Aba escondida nao precisa perguntar nada: ninguem esta vendo. Ao voltar,
    // consulta na hora, antes de retomar o intervalo.
    const aoMudarVisibilidade = () => {
      if (document.hidden) {
        parar();
      } else {
        recarregar();
        iniciar();
      }
    };

    const aoFocar = () => recarregar();

    if (!document.hidden) iniciar();
    window.addEventListener('focus', aoFocar);
    document.addEventListener('visibilitychange', aoMudarVisibilidade);

    return () => {
      parar();
      window.removeEventListener('focus', aoFocar);
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    };
  }, [recarregar]);

  const valor = useMemo(() => ({ lista, recarregar }), [lista, recarregar]);

  return React.createElement(PermissoesContexto.Provider, { value: valor }, children);
};

export const usePermissoes = () => {
  const { lista, recarregar } = useContext(PermissoesContexto);

  const pode = useCallback((modulo: string, acao: string): boolean => {
    // Sem lista nenhuma (sessão aberta antes desta versão, ou consulta ainda em
    // andamento) não se esconde nada: é melhor mostrar um botão que o servidor
    // recusa do que sumir com o sistema inteiro de quem tem acesso legítimo.
    if (lista.length === 0) return true;
    return lista.includes(`${modulo}.${acao}`);
  }, [lista]);

  const podeAlgumaDe = useCallback(
    (modulo: string, acoes: string[]): boolean => acoes.some((acao) => pode(modulo, acao)),
    [pode],
  );

  return { pode, podeAlgumaDe, recarregarPermissoes: recarregar };
};
